import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Paper, InputAdornment, Select, FormControl, InputLabel
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_URL = process.env.REACT_APP_API_URL || '';

function UserForm({ open, onClose, onSubmit, initial }) {
  const [form, setForm] = useState(initial || { username: '', password: '', role: '', employee_id: '' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setForm(initial || { username: '', password: '', role: '', employee_id: '' });
  }, [initial, open]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initial ? 'Edit User' : 'Add User'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Username" name="username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              label="Role"
              name="role"
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            >
              <MenuItem value="admin">admin</MenuItem>
              <MenuItem value="employee">employee</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Employee ID" name="employee_id" value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} fullWidth />
          <TextField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} variant="contained">{initial ? 'Save' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [showPasswordId, setShowPasswordId] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/admin/users`);
    const data = await res.json();
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAdd = async (form) => {
    await fetch(`${API_URL}/api/admin/add-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    setAddOpen(false);
    fetchUsers();
  };

  const handleOpenEdit = async (user) => {
    setEditUser({ ...user, loading: true });
    try {
      const response = await fetch(`${API_URL}/api/admin/user/${user.id}`);
      const userData = await response.json();
      setEditUser({ ...user, password: userData.password_hash });
    } catch (e) {
      setEditUser({ ...user, password: '' });
    }
  };

  const handleEdit = async (form) => {
    const payload = { ...form };
    if (form.password === editUser.password) {
      delete payload.password;
    }
    await fetch(`${API_URL}/api/admin/user/${editUser.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    setEditUser(null);
    fetchUsers();
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.username}?`)) return;
    await fetch(`${API_URL}/api/admin/user/${user.id}`, { method: 'DELETE' });
    fetchUsers();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" mb={2}>User Management</Typography>
      <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ mb: 2 }}>Add User</Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Password</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.employee_id}</TableCell>
                <TableCell>
                  ******
                  
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={e => { setMenuAnchor(e.currentTarget); setMenuUser(user); }}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { handleOpenEdit(menuUser); setMenuAnchor(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => { handleDelete(menuUser); setMenuAnchor(null); }}>Delete</MenuItem>
      </Menu>
      <UserForm open={addOpen} onClose={() => setAddOpen(false)} onSubmit={handleAdd} />
      <UserForm open={!!editUser} onClose={() => setEditUser(null)} onSubmit={handleEdit} initial={editUser} />
    </Box>
  );
} 